import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { 
  Search, 
  MapPin, 
  Star,
  Award,
  Shield
} from "lucide-react";
import { SEOHead } from "@/components/seo";
import { generateOrganizationSchema } from "@/components/seo/SchemaMarkup";
import { formatAgentRole } from "@/lib/utils";

interface AgentData {
  id: string;
  display_name: string | null;
  bio: string | null;
  photo_url: string | null;
  years_experience: number | null;
  specialties: string[] | null;
  service_areas: string[] | null;
  headline: string | null;
  agent_role: string | null;
  licensed_states: string[] | null;
  license_number: string | null;
  licensed_year: number | null;
  accreditations: string[] | null;
  slug: string | null;
}

const Agents = () => {
  const [agents, setAgents] = useState<AgentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    document.title = "NJ Real Estate Agents | Prime Realty Partners";
  }, []);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      // Load agents from secure public view
      const { data: agentsData, error } = await supabase
        .from("agents_public")
        .select("*");

      if (error) throw error;

      const mappedAgents: AgentData[] = (agentsData || []).map((agent: any) => ({
        id: agent.id,
        display_name: agent.display_name,
        bio: agent.bio,
        photo_url: agent.photo_url,
        years_experience: agent.years_experience,
        specialties: agent.specialties,
        service_areas: agent.service_areas,
        headline: agent.headline,
        agent_role: agent.agent_role,
        licensed_states: agent.licensed_states,
        license_number: agent.license_number,
        licensed_year: agent.licensed_year,
        accreditations: agent.accreditations,
        slug: agent.slug,
      }));

      setAgents(mappedAgents);
    } catch (error) {
      console.error("Error loading agents:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Check if any word in text starts with the search term
  const matchesWordStart = (text: string, search: string): boolean => {
    const words = text.toLowerCase().split(/[\s\-]+/);
    return words.some(word => word.startsWith(search));
  };

  // Helper: Calculate relevance score for an agent
  const calculateScore = (agent: AgentData, search: string): number => {
    if (!search) return 0;
    
    const searchLower = search.toLowerCase();
    const name = agent.display_name?.toLowerCase() || '';
    const nameParts = name.split(/\s+/);
    
    let score = 0;
    
    // Highest: Name starts with search term
    if (name.startsWith(searchLower)) {
      score += 100;
    }
    // High: First or last name starts with search term
    else if (nameParts.some(part => part.startsWith(searchLower))) {
      score += 80;
    }
    // Medium: Name contains search as a word boundary match
    else if (matchesWordStart(name, searchLower)) {
      score += 50;
    }
    
    // Lower: Specialty or service area word starts with search
    if (agent.specialties?.some(s => matchesWordStart(s, searchLower))) {
      score += 20;
    }
    if (agent.service_areas?.some(a => matchesWordStart(a, searchLower))) {
      score += 20;
    }
    
    return score;
  };

  // Filter agents using word-boundary matching
  const filteredAgents = agents.filter((agent) => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase().trim();
    const name = agent.display_name?.toLowerCase() || '';
    
    // Check if name matches (word boundary)
    if (matchesWordStart(name, searchLower)) return true;
    
    // Check specialties (word boundary)
    if (agent.specialties?.some(s => matchesWordStart(s, searchLower))) return true;
    
    // Check service areas (word boundary)
    if (agent.service_areas?.some(a => matchesWordStart(a, searchLower))) return true;
    
    return false;
  });

  // Sort by relevance when searching, alphabetical otherwise
  const sortedAgents = [...filteredAgents].sort((a, b) => {
    if (searchTerm.trim()) {
      // Sort by relevance score (higher first)
      const scoreA = calculateScore(a, searchTerm);
      const scoreB = calculateScore(b, searchTerm);
      if (scoreB !== scoreA) return scoreB - scoreA;
    }
    // Fall back to alphabetical by last name
    const lastA = a.display_name?.split(' ').pop()?.toLowerCase() || '';
    const lastB = b.display_name?.split(' ').pop()?.toLowerCase() || '';
    return lastA.localeCompare(lastB);
  });

  const organizationSchema = generateOrganizationSchema();

  // Generate ItemList schema for agents
  const agentsListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Prime Realty Partners Real Estate Agents",
    description: "Our team of experienced real estate professionals",
    numberOfItems: sortedAgents.length,
    itemListElement: sortedAgents.slice(0, 10).map((agent, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "RealEstateAgent",
        name: agent.display_name,
        jobTitle: formatAgentRole(agent.agent_role) || agent.headline || "Real Estate Agent",
        image: agent.photo_url,
        url: agent.slug ? `https://www.prpsj.com/agent/${agent.slug}` : undefined,
      },
    })),
  };

  return (
    <Layout>
      <SEOHead
        title="NJ Real Estate Agents | Prime Realty Partners"
        description="Meet the real estate agents at Prime Realty Partners. Our South Jersey team serves Cherry Hill, Moorestown, Haddonfield, Marlton, and surrounding communities."
        canonicalUrl="https://www.prpsj.com/agents"
        keywords="South Jersey real estate agents, NJ realtors, find a realtor South Jersey, Cherry Hill real estate agents, Camden County realtors, Prime Realty Partners agents"
        schema={[organizationSchema, agentsListSchema]}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Agents", url: "https://www.prpsj.com/agents" }]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 to-primary/5 py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Meet Our Agents
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Our team of experienced professionals is dedicated to helping you 
              find your dream home or sell your property for the best price.
            </p>
            
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search by name, specialty, or area..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Agents Grid */}
      <section className="py-16">
        <div className="container">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
          ) : sortedAgents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {searchTerm 
                  ? "No agents found matching your search." 
                  : "No agents available at this time."}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedAgents.map((agent) => (
                <Card key={agent.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-square relative bg-muted">
                    {agent.photo_url ? (
                      <img
                        src={agent.photo_url}
                        alt={`${agent.display_name || "Agent"} — Real Estate Agent at Prime Realty Partners`}
                        width={400}
                        height={400}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-6xl font-bold text-muted-foreground/30">
                        {agent.display_name?.charAt(0) || "A"}
                      </div>
                    )}
                    {/* Role Badge */}
                    {agent.agent_role && (
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-primary text-primary-foreground shadow-md">
                          <Shield className="h-3 w-3 mr-1" />
                          {formatAgentRole(agent.agent_role)}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl font-bold mb-1">
                      {agent.display_name || "Agent"}
                    </h3>
                    {agent.headline && (
                      <p className="text-sm text-primary mb-2">{agent.headline}</p>
                    )}
                    
                    {/* Experience & License Info */}
                    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mb-3">
                      {agent.years_experience && (
                        <span className="flex items-center gap-1">
                          <Star className="h-4 w-4" />
                          {agent.years_experience} yrs exp
                        </span>
                      )}
                      {agent.licensed_year && (
                        <span>• Licensed since {agent.licensed_year}</span>
                      )}
                    </div>

                    {/* Licensed States */}
                    {agent.licensed_states && agent.licensed_states.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {agent.licensed_states.map((state) => (
                          <Badge key={state} variant="outline" className="text-xs">
                            {state}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Service Areas */}
                    {agent.service_areas && agent.service_areas.length > 0 && (
                      <div className="flex items-start gap-2 text-sm text-muted-foreground mb-3">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{agent.service_areas.slice(0, 3).join(", ")}</span>
                      </div>
                    )}

                    {/* Accreditations */}
                    {agent.accreditations && agent.accreditations.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {agent.accreditations.slice(0, 4).map((acc) => (
                          <Badge key={acc} variant="secondary" className="text-xs gap-1">
                            <Award className="h-3 w-3" />
                            {acc}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Specialties */}
                    {agent.specialties && agent.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {agent.specialties.slice(0, 3).map((specialty, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {specialty}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      {/* View Profile Button */}
                      {agent.slug && (
                        <Link to={`/agent/${agent.slug}`}>
                          <Button className="w-full">
                            View Profile
                          </Button>
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Join Our Team
          </h2>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Are you a licensed real estate agent looking to grow your career? 
            Prime Realty Partners offers industry-leading support, training, and tools.
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary">
              Learn More About Joining
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Agents;
